/**
 * SessionRecapContent — shared presentational component for AI-generated
 * session recap highlights.
 *
 * Used by both the in-session Prep tab and the patient profile Overview tab.
 * Renders bulleted highlights with optional citation links to Healthie
 * charting notes. Handles loading, empty, and error states.
 *
 * Tailwind/shadcn — no Chakra dependencies.
 */
import { datadogLogs } from '@datadog/browser-logs';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import type { SessionRecap } from '@web-app/types/session-prep';
import { SESSION_RECAP_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';
import { formatHealthieDisplayDate } from '@web-app/utils/date-time.utils';
import { buildCitationUrl } from '@web-app/utils/session-recap/session-recap.utils';
import { FC, useEffect } from 'react';
import { TbLink } from 'react-icons/tb';

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function RecapSkeleton() {
  return (
    <div data-testid="session-recap-skeleton" className="flex flex-col gap-3">
      <div className="h-3 w-32 animate-pulse rounded bg-muted" />
      <div className="h-3 w-full animate-pulse rounded bg-muted" />
      <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
      <div className="h-3 w-4/6 animate-pulse rounded bg-muted" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SessionRecapContentProps {
  sessionRecap: SessionRecap | null;
  error: Error | null;
  isLoading: boolean;
  patientId?: string | null;
  /** Hide the "Last Session Recap" heading and date sub-header. Use when the
   *  parent already displays this context (e.g., the profile overview tab). */
  hideHeader?: boolean;
}

/**
 * Renders session recap highlights with citation links.
 *
 * Pure presentational — data fetching is the caller's responsibility.
 * Both the in-session Prep tab and the profile Overview tab pass data
 * from `useSessionRecap(patientId)`.
 */
export const SessionRecapContent: FC<SessionRecapContentProps> = ({
  sessionRecap,
  error,
  isLoading,
  patientId,
  hideHeader = false,
}) => {
  const { trackEvent } = useAnalytics();

  // Structured logging — same as the legacy SessionRecapCard
  useEffect(
    function logRecapRenderState() {
      if (isLoading) return;

      if (error) {
        datadogLogs.logger.warn(
          `${SESSION_RECAP_LOG_PREFIX} Recap load failed`,
          { err: error, patientId },
        );
      } else if (sessionRecap && sessionRecap.highlights.length > 0) {
        datadogLogs.logger.info(`${SESSION_RECAP_LOG_PREFIX} Recap rendered`, {
          patientId,
          highlightsCount: sessionRecap.highlights.length,
          lastAppointmentDate: sessionRecap.lastAppointmentDate,
        });
      }
    },
    [isLoading, error, sessionRecap, patientId],
  );

  if (isLoading) {
    return <RecapSkeleton />;
  }

  // No recap available (first session or no previous appointments)
  if (!sessionRecap || sessionRecap.highlights.length === 0) {
    return (
      <div data-testid="session-recap-content">
        {!hideHeader && (
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Last Session Recap
          </p>
        )}
        <p className="py-4 text-sm text-muted-foreground">
          {error ? 'Failed to load recaps.' : 'No recap available'}
        </p>
      </div>
    );
  }

  const formattedDate = formatHealthieDisplayDate(
    sessionRecap.lastAppointmentDate,
  );

  return (
    <div data-testid="session-recap-content" className="flex flex-col gap-4">
      {!hideHeader && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Last Session Recap
          </p>

          {formattedDate ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Recap from {formattedDate}
            </p>
          ) : null}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {sessionRecap.highlights.map((highlight, index) => {
          const citationUrl = highlight.citations?.[0]
            ? buildCitationUrl(highlight.citations[0], patientId)
            : null;

          return (
            <div key={index} className="flex items-start gap-2">
              <span className="text-sm font-bold text-primary">&bull;</span>
              <span className="text-sm text-foreground">
                {highlight.content}
                {citationUrl ? (
                  <a
                    href={citationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 inline-block align-middle text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      trackEvent(EVENT_NAMES.SESSION_RECAP_CITATION_CLICKED, {
                        patientId,
                        highlightIndex: index,
                        sourceChartingItemId: highlight.citations[0]?.source_id,
                      });
                    }}
                  >
                    <TbLink className="relative -top-px inline size-4" />
                  </a>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

SessionRecapContent.displayName = 'SessionRecapContent';
